import { read, write } from './_base';
import { billingApi } from '../../api/axiosInstance';
import {
  Billing_Documents_Url,
  Billing_Document_Url,
  Billing_Document_Events_Url,
  Billing_Document_Pdf_Url,
  Billing_Document_Issue_Url,
  Billing_Document_Draft_Url,
  Billing_Einvoice_Url,
  Billing_Einvoice_Export_Url,
  Billing_Document_Void_Url,
  Billing_Document_WriteOff_Url,
  Billing_Document_Convert_Url,
  Billing_Document_Accept_Url,
  Billing_Document_Decline_Url,
  Billing_Document_Deliver_Url,
  Billing_Document_ResolvePlaceholder_Url,
  Billing_Documents_AiDraft_Url,
} from '../../api/api.endpoints';
import {
  BillingDocument,
  CreateDocumentPayload,
  DocumentEvent,
  DocumentListResponse,
} from '../../schema/billing/document';
import { DocType, DocStatus } from '../../schema/billing/common';

export const listDocuments = (params?: { type?: DocType; status?: DocStatus }) =>
  read<DocumentListResponse>(Billing_Documents_Url, 'Failed to load documents', params).then(
    (r) => r.documents ?? [],
  );

export const getDocument = (id: string) =>
  read<BillingDocument>(Billing_Document_Url(id), 'Failed to load document');

export const getDocumentEvents = (id: string) =>
  read<{ events: DocumentEvent[] }>(Billing_Document_Events_Url(id), 'Failed to load timeline').then(
    (r) => r.events ?? [],
  );

/** Fetch the server-rendered PDF. The backend returns a JSON envelope with the
 *  filename + base64 content (not a raw binary), so callers decode it. */
export const getDocumentPdf = async (id: string): Promise<{ file_name: string; content_base64: string }> => {
  const { data } = await billingApi.get<{ file_name: string; content_base64: string }>(Billing_Document_Pdf_Url(id));
  return data;
};

export const createDocument = (body: CreateDocumentPayload) =>
  write<BillingDocument>('post', Billing_Documents_Url, {
    body,
    errorTitle: 'Could not save draft',
    successTitle: 'Draft saved',
  });

/**
 * Save the editor's view of an opened DRAFT over the stored one, in place —
 * same id, so a retry never makes a second draft and Issue issues what is on
 * screen. doc_type is stamped at create and is not sent.
 */
export const reviseDraft = (id: string, body: CreateDocumentPayload) => {
  const { doc_type: _docType, ...rest } = body;
  return write<BillingDocument>('put', Billing_Document_Draft_Url(id), { body: rest, errorTitle: 'Could not save draft' });
};

export interface EinvoiceStatus {
  applicable: boolean;
  status: 'PENDING' | 'EXPORTED' | 'IRN_GENERATED' | 'CANCELLED' | string;
  irn: string | null;
  past_window: boolean;
  exported_at: string | null;
}

/** e-Invoice state of one document. Silent: a 422 just means "not applicable". */
export const getEinvoiceStatus = (id: string) =>
  read<EinvoiceStatus>(Billing_Einvoice_Url(id), 'Failed to load e-invoice status', undefined, true);

/** Build (and record) the NIC INV-01 JSON for upload to the IRP. */
export const exportEinvoice = (id: string, confirm_past_window = false) =>
  write<{ document_id: string; status: string; payload: unknown }>('post', Billing_Einvoice_Export_Url(id), {
    body: { confirm_past_window },
    errorTitle: 'Could not export e-invoice',
  });

export const issueDocument = (id: string) =>
  write<BillingDocument>('post', Billing_Document_Issue_Url(id), {
    errorTitle: 'Issue failed',
    successTitle: 'Document issued',
    successFallback: 'Number assigned, ledger posted, now immutable.',
  });

export const voidDocument = (id: string, reason_code: string) =>
  write('post', Billing_Document_Void_Url(id), {
    body: { reason_code },
    errorTitle: 'Void failed',
    successTitle: 'Voided',
  });

export const writeOffDocument = (id: string, reason_code: string) =>
  write('post', Billing_Document_WriteOff_Url(id), {
    body: { reason_code },
    errorTitle: 'Write-off failed',
    successTitle: 'Written off',
  });

export const convertDocument = (id: string) =>
  write('post', Billing_Document_Convert_Url(id), { errorTitle: 'Convert failed', successTitle: 'Converted to invoice' });

export const acceptQuote = (id: string) =>
  write('post', Billing_Document_Accept_Url(id), { errorTitle: 'Accept failed', successTitle: 'Quote accepted' });

export const declineQuote = (id: string) =>
  write('post', Billing_Document_Decline_Url(id), { errorTitle: 'Decline failed', successTitle: 'Quote declined' });

export const deliverDocument = (id: string, body: { channel: 'EMAIL' | 'WHATSAPP'; to: string; message?: string }) =>
  write('post', Billing_Document_Deliver_Url(id), { body, errorTitle: 'Send failed', successTitle: 'Sent' });

export const resolvePlaceholder = (id: string, body: unknown) =>
  write('post', Billing_Document_ResolvePlaceholder_Url(id), { body, errorTitle: 'Could not resolve', successTitle: 'Resolved' });

/** Delete a DRAFT document. Success is surfaced via a dialog, not a toast. */
export const deleteDocument = (id: string) =>
  write<{ deleted: boolean; id: string }>('delete', Billing_Document_Url(id), {
    errorTitle: 'Could not delete',
  });

export const createAiDraft = (prompt: string) =>
  write<BillingDocument>('post', Billing_Documents_AiDraft_Url, {
    body: { prompt },
    errorTitle: 'AI draft failed',
    successTitle: 'AI draft created',
    successFallback: 'Review the placeholders, then issue.',
  });
